// Web Audio API sound system — synthesized so we ship zero audio files.
// Provides a single AudioContext lazily and small helpers for the game UI.

const KEY_MUTED = "chronos.sound.muted";

let ctx = null;
let masterGain = null;

function ensureCtx() {
  if (typeof window === "undefined") return null;
  if (!ctx) {
    try {
      const AC = window.AudioContext || window.webkitAudioContext;
      if (!AC) return null;
      ctx = new AC();
      masterGain = ctx.createGain();
      masterGain.gain.value = isMuted() ? 0 : 0.35;
      masterGain.connect(ctx.destination);
    } catch (e) { return null; }
  }
  // Some browsers suspend the context until a user gesture. Resume best-effort.
  if (ctx.state === "suspended") ctx.resume().catch(() => {});
  return ctx;
}

export function isMuted() {
  try { return localStorage.getItem(KEY_MUTED) === "1"; } catch (e) { return false; }
}

export function setMuted(muted) {
  try { localStorage.setItem(KEY_MUTED, muted ? "1" : "0"); } catch (e) { /* ignore */ }
  if (masterGain) masterGain.gain.value = muted ? 0 : 0.35;
}

function tone({ freq = 440, type = "sine", duration = 0.18, attack = 0.005, release = 0.15, gain = 0.5 }) {
  const c = ensureCtx();
  if (!c) return;
  const osc = c.createOscillator();
  const g = c.createGain();
  osc.type = type;
  osc.frequency.value = freq;
  const now = c.currentTime;
  g.gain.setValueAtTime(0, now);
  g.gain.linearRampToValueAtTime(gain, now + attack);
  g.gain.exponentialRampToValueAtTime(0.0001, now + attack + release);
  osc.connect(g);
  g.connect(masterGain);
  osc.start(now);
  osc.stop(now + attack + release + 0.02);
}

/** Soft click when picking up a puzzle piece. */
export function playClick() {
  tone({ freq: 620, type: "triangle", duration: 0.07, attack: 0.002, release: 0.06, gain: 0.35 });
}

/** Positive chime when an object is correctly placed. */
export function playPlace() {
  const c = ensureCtx();
  if (!c) return;
  // Two-note major-third chime
  tone({ freq: 660, type: "sine", release: 0.18, gain: 0.4 });
  setTimeout(() => tone({ freq: 990, type: "sine", release: 0.22, gain: 0.35 }), 80);
}

/** Gentle error tone when a piece is dropped on the wrong slot. */
export function playError() {
  tone({ freq: 260, type: "sawtooth", release: 0.18, gain: 0.3 });
}

/** Victory arpeggio when a chapter is solved. */
export function playWin() {
  const notes = [523.25, 659.25, 783.99, 1046.5]; // C5 E5 G5 C6
  notes.forEach((f, i) => setTimeout(() => tone({
    freq: f, type: "triangle", release: 0.35, gain: 0.35,
  }), i * 110));
}

/** Whoosh-like descending tone when a hint appears. */
export function playHint() {
  const c = ensureCtx();
  if (!c) return;
  const osc = c.createOscillator();
  const g = c.createGain();
  osc.type = "sine";
  const now = c.currentTime;
  osc.frequency.setValueAtTime(880, now);
  osc.frequency.exponentialRampToValueAtTime(420, now + 0.4);
  g.gain.setValueAtTime(0, now);
  g.gain.linearRampToValueAtTime(0.28, now + 0.02);
  g.gain.exponentialRampToValueAtTime(0.0001, now + 0.42);
  osc.connect(g); g.connect(masterGain);
  osc.start(now);
  osc.stop(now + 0.45);
}

// ============ AMBIENT PADS (per era) ============
// Very quiet drone pads. One oscillator + slow LFO on gain for gentle breathing.
const AMBIENT_PRESETS = {
  antiquity:   { root: 174.61, fifth: 261.63, type: "sine",     gain: 0.055 }, // F3 + C4, warm
  medieval:    { root: 130.81, fifth: 196.00, type: "triangle", gain: 0.06  }, // C3 + G3, austere
  renaissance: { root: 220.00, fifth: 329.63, type: "sine",     gain: 0.05  }, // A3 + E4, hopeful
  future:      { root: 155.56, fifth: 233.08, type: "sawtooth", gain: 0.035 }, // Eb3 + Bb3, dreamy (softer)
};

let ambientNodes = null; // { osc1, osc2, gainNode, lfo, lfoGain, eraKey }

export function stopAmbient() {
  if (!ambientNodes) return;
  const c = ctx;
  const { osc1, osc2, gainNode, lfo } = ambientNodes;
  try {
    if (c) {
      const now = c.currentTime;
      gainNode.gain.cancelScheduledValues(now);
      gainNode.gain.setValueAtTime(gainNode.gain.value, now);
      gainNode.gain.linearRampToValueAtTime(0.0001, now + 0.4);
      osc1.stop(now + 0.5);
      osc2.stop(now + 0.5);
      lfo.stop(now + 0.5);
    }
  } catch (e) { /* ignore */ }
  ambientNodes = null;
}

export function playAmbient(eraKey) {
  const preset = AMBIENT_PRESETS[eraKey];
  if (!preset) { stopAmbient(); return; }
  if (ambientNodes && ambientNodes.eraKey === eraKey) return; // already playing
  stopAmbient();
  const c = ensureCtx();
  if (!c) return;

  const gainNode = c.createGain();
  gainNode.gain.value = 0.0001;
  gainNode.connect(masterGain);

  // slow LFO for gentle breathing
  const lfo = c.createOscillator();
  lfo.frequency.value = 0.12; // ~8s cycle
  const lfoGain = c.createGain();
  lfoGain.gain.value = preset.gain * 0.4;
  lfo.connect(lfoGain);
  lfoGain.connect(gainNode.gain);

  const osc1 = c.createOscillator();
  osc1.type = preset.type;
  osc1.frequency.value = preset.root;
  const osc2 = c.createOscillator();
  osc2.type = preset.type;
  osc2.frequency.value = preset.fifth;
  osc1.connect(gainNode);
  osc2.connect(gainNode);

  const now = c.currentTime;
  gainNode.gain.linearRampToValueAtTime(preset.gain, now + 1.5); // fade-in
  osc1.start(now);
  osc2.start(now);
  lfo.start(now);
  ambientNodes = { osc1, osc2, gainNode, lfo, lfoGain, eraKey };
}
